// moms-harness:anchor v2
// Spec: docs/spec/settlement/SPEC-settlement-mwp-mlt-sku-attribute-registration-C.yaml
// JIRA: LOGISPRD-13286, LOGISPRD-13480, LOGISPRD-13852, LOGISPRD-14507
import { SkuAttributeParams } from '../queries/useGetSkuAttributeList'

/**
 * URL 쿼리 → 복원 조회 조건. 배열로 온 값은 첫 값만 쓴다 (BE 가 단일 값 파라미터다).
 * **`skuIds` 만 예외로 전부 쓴다** — 다건 조건이다 (LOGISPRD-13852).
 *
 * 상품단위(`unitCode`)는 **읽지 않는다.** 필터에서 뺐으므로 옛 URL 에 남은 값을 복원하면
 * 입력칸 없이 결과만 걸러지는 보이지 않는 조건이 된다.
 *
 * ### 화주 조건이 없으면 **`undefined`** 다 — 객체가 아니다
 *
 * 필터의 자동조회가 `onSearch(initialParams ?? { shipperCode: defaultShipper })` 라, 여기서 **항상
 * 객체를 돌려주면 `??` 오른쪽이 영영 안 걸린다.** 그러면 첫 진입에서
 *
 * - 전 필드가 `undefined` 인 조건으로 `onSearch` 가 불려 `hasSearchCondition` 이 false → **조회가 안 나가고**
 * - 그런데 `onFormReady` 는 기본 화주를 채워 **화면에는 화주가 선택돼 보이며**
 * - `targetShipper` 가 빈 문자열이라 **[추가]·[엑셀 업로드] 가 「화주명을 선택하세요」로 막힌다**
 *
 * 초판이 그 상태였다. MA02 `fromSearchQuery` 는 같은 자리에서 `undefined` 를 돌려주는데
 * (`if (!serviceTypes && !shipperCodes) return undefined`) 그 한 줄을 옮기지 못한 것이다.
 */
export const fromQuery = (qs: Record<string, unknown>): SkuAttributeParams | undefined => {
  const one = (v: unknown) => (Array.isArray(v) ? (v[0] != null ? String(v[0]) : undefined) : typeof v === 'string' && v ? v : undefined)
  const many = (v: unknown) => (Array.isArray(v) ? v.map(String) : typeof v === 'string' && v ? [v] : undefined)

  const serviceTypes = many(qs.serviceTypes)
  const shipperCode = one(qs.shipperCode)
  // 목록 조회 URL 은 콤마로 이어 붙인다(`skuIds=A,B`) — 반복 파라미터와 둘 다 받는다
  const skuIds = many(qs.skuIds)
    ?.flatMap((v) => v.split(','))
    .map((v) => v.trim())
    .filter(Boolean)
  // 화주 축도 SKU ID 도 없으면 복원할 조건이 아니다 — 조회는 둘 중 하나가 필수다
  if (!serviceTypes?.length && !shipperCode && !skuIds?.length) return undefined

  return {
    serviceTypes,
    shipperCode,
    barcode: one(qs.barcode),
    itemCode: one(qs.itemCode),
    itemName: one(qs.itemName),
    brandName: one(qs.brandName),
    color: one(qs.color),
    styleNumber: one(qs.styleNumber),
    skuIds: skuIds?.length ? skuIds : undefined,
  }
}

/**
 * 복원 조회 조건 → **필터 폼 값**. [fromQuery] 의 역방향이고 `toParams` 와 키가 1:1 로 맞물린다.
 *
 * ### 왜 필요한가 — 조회 조건과 화면이 어긋났다
 *
 * 초판은 `onFormReady` 가 `shipperNames` 하나만 채웠다. 그런데 초기 조회는 `initialParams`
 * **전체**로 나간다. 그래서
 *
 * - 바코드로 조회 → URL 에 `barcode` 가 남음 → 새로고침 → **목록은 걸러졌는데 입력칸은 비어 있음**
 * - 그 상태에서 [조회]를 다시 누르면 빈 바코드를 읽어 **조건이 조용히 풀린다**
 * - `?serviceTypes=MWP` 만 있는 URL → 기본 화주 하나가 선택된 것처럼 보이지만 실제 조회는 MWP 전 화주
 *
 * MA02 `ProductManagementFilter` 가 같은 자리에서 `toFilterFormValues(initialParams)` 로 전체를
 * 되돌린다. 그 형태를 따른다.
 *
 * ### 텍스트 조건 여섯 칸은 **배열**로 심는다 (LOGISPRD-14507)
 *
 * 그 칸들은 `mfs.FormInput`(plain 아님, 다건 입력)이라 폼 값이 `string[]` 이어야 한다 — 위젯이 렌더 중에
 * 값에 `.join` 을 부른다. 초판은 조회 조건의 단건 문자열을 그대로 심어, 텍스트 조건이 남은 URL 로
 * 들어오면 **필터가 렌더 중에 터졌다.** MA02 도 배열로 심는다. 조회 쪽(`toParams`)은 배열에서 첫 값을 꺼내므로
 * 왕복이 맞물린다.
 */
export const toFilterFormValues = (params: SkuAttributeParams): Record<string, unknown> => {
  const values: Record<string, unknown> = {}
  if (params.shipperCode) values.shipperNames = [params.shipperCode]
  if (params.serviceTypes?.length) values.logisticsServiceTypes = params.serviceTypes
  if (params.barcode) values['f-barcode'] = [params.barcode]
  if (params.itemCode) values['f-itemCode'] = [params.itemCode]
  if (params.itemName) values['f-itemName'] = [params.itemName]
  if (params.brandName) values['f-brandName'] = [params.brandName]
  if (params.color) values['f-color'] = [params.color]
  if (params.styleNumber) values['f-styleNumber'] = [params.styleNumber]
  if (params.skuIds?.length) values['f-skuId'] = params.skuIds
  return values
}
